import { useEffect, useState, type ReactNode } from 'react'
import { MagnifyingGlass, X } from '@phosphor-icons/react'
import { cn } from '@/lib/cn'
import { Input, Select, type SelectOption } from '@/ui'

/**
 * The filter row above a console table: labelled compact controls that wrap on tablets, and a
 * "Clear" when anything is set. Put it in DataTable's `filters` slot or above a custom list.
 */
export function FilterBar({
  children,
  onClear,
  active,
  className,
}: {
  children: ReactNode
  onClear?: () => void
  /** Show "Clear" when true. */
  active?: boolean
  className?: string
}) {
  return (
    <div role="group" aria-label="Filters" className={cn('flex w-full flex-wrap items-end gap-2', className)}>
      {children}
      {onClear && active && (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex h-9 items-center gap-1 rounded-chip px-2 font-mono text-[11px] font-bold uppercase tracking-label text-muted hover:text-ink"
        >
          <X aria-hidden="true" /> Clear
        </button>
      )}
    </div>
  )
}

function Labelled({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn('flex min-w-[9rem] flex-col gap-1', className)}>
      <span className="font-mono text-[10px] font-bold uppercase tracking-label text-muted">{label}</span>
      {children}
    </label>
  )
}

export function SelectFilter<V extends string>({
  label,
  value,
  onChange,
  options,
  allLabel = 'All',
  className,
}: {
  label: string
  value: V | ''
  onChange: (v: V | '') => void
  options: ReadonlyArray<SelectOption<V>>
  allLabel?: string
  className?: string
}) {
  return (
    <Labelled label={label} className={className}>
      <Select<V | ''>
        aria-label={label}
        value={value}
        onChange={onChange}
        options={[{ value: '' as V | '', label: allLabel }, ...options]}
        className="h-9 text-[14px]"
      />
    </Labelled>
  )
}

/** Debounced text search (server filters). */
export function SearchFilter({
  label,
  value,
  onChange,
  placeholder,
  delay = 300,
  className,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  delay?: number
  className?: string
}) {
  const [text, setText] = useState(value)
  const [prev, setPrev] = useState(value)
  if (value !== prev) {
    setPrev(value)
    setText(value)
  }
  useEffect(() => {
    if (text === value) return
    const t = setTimeout(() => onChange(text.trim()), delay)
    return () => clearTimeout(t)
  }, [text, value, onChange, delay])
  return (
    <Labelled label={label} className={cn('min-w-[12rem]', className)}>
      <Input
        type="search"
        aria-label={label}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        leading={<MagnifyingGlass />}
        className="h-9 text-[14px]"
      />
    </Labelled>
  )
}

export function DateFilter({
  label,
  value,
  onChange,
  className,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  className?: string
}) {
  return (
    <Labelled label={label} className={className}>
      <Input type="date" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 text-[14px]" />
    </Labelled>
  )
}

/** Yes / No / Any as a select. */
export function BoolFilter({
  label,
  value,
  onChange,
  yes = 'Yes',
  no = 'No',
}: {
  label: string
  value: boolean | undefined
  onChange: (v: boolean | undefined) => void
  yes?: string
  no?: string
}) {
  return (
    <SelectFilter<'true' | 'false'>
      label={label}
      value={value === undefined ? '' : value ? 'true' : 'false'}
      onChange={(v) => onChange(v === '' ? undefined : v === 'true')}
      options={[
        { value: 'true', label: yes },
        { value: 'false', label: no },
      ]}
      allLabel="Any"
    />
  )
}
